import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Vehicle, vehiclesApi } from '@/services/api/vehicles';
import { VehicleStatus } from '@/types/common';
import { formatRupiah, getUserEmail } from '@/utils';
import { SideBarModal } from '@/components/Modal';
import { Button } from '@/components';
import {
  showSuccessToast,
  showErrorToast,
} from '@/components/toast/globalToast';
import { extractApiErrorMessage } from '@/utils';

interface VehicleIdleCostHeaderProps {
  vehicle: Vehicle;
  className?: string;
  // Called after a successful settle/resume so the parent can refetch.
  onChanged?: () => void;
}

/**
 * Amber idle-cost band shown at the top of the vehicle detail page.
 * Rendered only for IDLE vehicles (returns null otherwise). Values are
 * computed server-side:
 *   idle_cost = Harga Beli (daily) × Idle Days
 * Settled units (OEM deal paid off, cost_settled_at set) show "—" and stop
 * accruing; the Settle/Resume button flips that per unit.
 */
const VehicleIdleCostHeader: React.FC<VehicleIdleCostHeaderProps> = ({
  vehicle,
  className = '',
  onChanged,
}) => {
  const [confirmMode, setConfirmMode] = useState<'settle' | 'resume' | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (vehicle.status !== VehicleStatus.IDLE) return null;

  const idleDays = vehicle.idleDays ?? 0;
  const hargaBeli = vehicle.modelPurchasePrice ?? null;
  // Settled: OEM deal paid off — idleCost comes back null, days keep counting
  const isSettled = vehicle.costSettledAt != null;

  const handleConfirm = async () => {
    if (!confirmMode || !vehicle.ID) return;
    setIsSubmitting(true);
    try {
      await vehiclesApi.settleIdleCost(String(vehicle.ID), {
        settled: confirmMode === 'settle',
        updatedBy: getUserEmail(),
      });
      showSuccessToast(
        'Success',
        confirmMode === 'settle'
          ? 'Unit marked as settled — idle cost stopped'
          : 'Unit resumed idle cost accrual'
      );
      setConfirmMode(null);
      onChanged?.();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to update idle cost settlement')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`mb-4 rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 ${className}`}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">
              Idle Cost
            </div>
            {isSettled && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                Settled
              </span>
            )}
          </div>
          <div className="mt-1 text-3xl font-bold tabular-nums text-amber-900">
            {isSettled ? '—' : formatRupiah(vehicle.idleCost)}
          </div>
          <div className="mt-1 text-xs text-amber-700">
            {isSettled
              ? 'OEM deal paid off — idle cost no longer accrues'
              : `${formatRupiah(hargaBeli)} × ${idleDays} Days`}
          </div>
          <button
            type="button"
            onClick={() => setConfirmMode(isSettled ? 'resume' : 'settle')}
            className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
          >
            <i
              className={`${
                isSettled ? 'ri-refresh-line' : 'ri-money-dollar-circle-line'
              } mr-1`}
            />
            {isSettled ? 'Resume Idle Cost' : 'Settle Idle Cost'}
          </button>
        </div>

        <div className="flex flex-col gap-1 sm:min-w-[240px]">
          <div className="flex items-center justify-between gap-8 border-b border-amber-100 py-1">
            <span className="text-xs text-amber-700">Idle Since</span>
            <span className="text-xs font-semibold text-amber-900">
              {vehicle.idleSince
                ? dayjs(vehicle.idleSince).format('D MMMM YYYY')
                : '-'}
            </span>
          </div>
          <div className="flex items-center justify-between gap-8 border-b border-amber-100 py-1">
            <span className="text-xs text-amber-700">Idle Days</span>
            <span className="text-xs font-semibold text-amber-900">
              {idleDays} Days
            </span>
          </div>
          <div className="flex items-center justify-between gap-8 py-1">
            <span className="text-xs text-amber-700">Harga Beli per Day</span>
            <span className="text-xs font-semibold text-amber-900">
              {formatRupiah(hargaBeli)}
            </span>
          </div>
        </div>
      </div>

      <SideBarModal
        isOpen={confirmMode !== null}
        onClose={() => setConfirmMode(null)}
        position="center"
        width="md"
        maxHeight="auto"
        showCloseButton={false}
        header={
          <div className="flex items-start gap-4">
            <div
              className={`flex h-[40px] w-[50px] items-center justify-center rounded-lg ${
                confirmMode === 'settle'
                  ? 'bg-emerald-50 text-emerald-500'
                  : 'bg-orange-50 text-orange-500'
              }`}
            >
              <i
                className={`${
                  confirmMode === 'settle'
                    ? 'ri-money-dollar-circle-fill'
                    : 'ri-refresh-fill'
                } text-[16px]`}
              />
            </div>
            <div>
              <div className="text-xl font-semibold text-gray-900">
                {confirmMode === 'settle'
                  ? `Settle idle cost for ${vehicle.platNomor}?`
                  : `Resume idle cost for ${vehicle.platNomor}?`}
              </div>
              <div className="mt-1 text-sm text-gray-500">
                {confirmMode === 'settle'
                  ? 'This unit will stop accruing idle cost (the OEM deal covering it is paid off). Idle days keep counting.'
                  : 'This unit will start accruing idle cost again.'}
              </div>
            </div>
          </div>
        }
        footer={
          <div className="flex w-full justify-end gap-2">
            <Button
              onClick={() => setConfirmMode(null)}
              className="rounded-xl border border-gray-200 bg-white px-6 py-2 text-gray-600 hover:bg-gray-50"
              variant={'secondary'}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={isSubmitting}
              className="rounded-xl px-6 py-2 text-white"
              variant={'primary'}
            >
              {isSubmitting
                ? 'Saving...'
                : confirmMode === 'settle'
                  ? 'Settle Idle Cost'
                  : 'Resume Idle Cost'}
            </Button>
          </div>
        }
      >
        <div className="h-0" />
      </SideBarModal>
    </div>
  );
};

export default VehicleIdleCostHeader;
